<template>
    <div>
        <div ref="turnstile" id="turnstile"></div>
        <HarmonyLoader v-if="loading" />
        <div class="page-background" v-if="!loading">
            <div class="background-layer" :style="getPageBackgroundStyle()"></div>
            <div class="background" :style="getBackgroundStyle()" :class="{ 'background-opacity': true }">
                <EditorNavbar :harmony="harmony" v-if="!loading && editor" @layout-change="changeSiteLayout($event)" />

                <!-- Editor Menu Drawer -->
                <v-navigation-drawer v-model="menu_editor" absolute right temporary
                    :style="{ height: 'auto', overflowY: 'auto', maxHeight: maxHeight }"
                    class="floating-drawer rounded-xl mt-2 mr-2"
                    :width="select === 'socials' ? 280 : '256'" v-if="editor">

                    <div class="px-4">
                        <template v-if="select == 'logo'">
                            <EditorLogoPanel
                                :harmony="harmony"
                                :site-setting="site_setting"
                                :loading-save="loadingSave"
                                :patterns="patterns"
                                :initial-select-color-gradient="selectColorGradient"
                                :initial-rotation-angle="rotationAngle"
                                :select-background="selectBackground"
                                @update:selectBackground="selectBackground = $event"
                                @save-settings="saveSettings"
                                @remove-logo="removeLogo(harmony.id)"
                                @remove-background="removeBackground"
                                @remove-background-color="removeBackgroundColor"
                                @image-uploaded="handleImageUploaded"
                                @update-gradient="updateGradientFromPanel"
                                @rotate-image="updateRotationAngle"
                                @set-pattern="setPattern"
                            />
                        </template>

                        <template v-if="select == 'title'">
                            <EditorTitlePanel
                                :harmony="harmony"
                                :site-setting="site_setting"
                                :loading-save="loadingSave"
                                :fonts-options="fontsOptions"
                                :harmony-rules="harmonyRules"
                                @update:name="updateHarmonyName"
                                @update:title-font="updateTitleFont"
                                @update:title-color="updateTitleColor"
                                @save-nick-settings="saveNickSettings"
                            />
                        </template>

                        <template v-if="select == 'description'">
                            <EditorDescriptionPanel
                                :site-setting="site_setting"
                                :loading-save="loadingSave"
                                @update:description="updateDescription"
                                @update:desc-color="updateDescColor"
                                @update:description-style="updateDescriptionStyleFromPanel"
                                @save-settings="saveSettings"
                                @description-input="handleDescriptionInput"
                            />
                        </template>

                        <template v-if="select == 'card'">
                            <EditorCardPanel
                                :harmony="harmony"
                                :site-setting="site_setting"
                                :card-select="cardSelect"
                                :platforms="platforms"
                                :loading-save="loadingSave"
                                :items-for-upload="itemsForUpload"
                                :colors="colors"
                                :fonts-options="fontsOptions"
                                :menu-editor="menu_editor"
                                @save-settings="saveSettings"
                                @save-settings-auto="saveSettingsAuto"
                                @image-uploaded="handleImageUploaded"
                                @video-uploaded="handleVideoUploaded"
                                @remove-background="removeBackground"
                                @remove-video="removeVideo"
                                @remove-gallery="removeGallery"
                                @remove-schedules="removeSchedules"
                                @remove-last-schedule="removeLastSchedule"
                                @select-color="selectColor"
                                @add-platform="addPlatform"
                            />
                        </template>

                        <template v-if="select == 'socials'">
                            <EditorSocialsPanel
                                :site-setting="site_setting"
                                :socials="socials"
                                :list-socials="list_socials"
                                :loading-save="loadingSave"
                                :socials-bar="socialsBar"
                                @set-social-style="setSocialStyle"
                                @save-social="saveSocial"
                                @delete-social="deleteSocial"
                            />
                        </template>


                        <template v-if="select == 'add-card'">
                            <EditorAddCardPanel
                                :loading-save="loadingSave"
                                :selected-layout="selectedLayout"
                                @add-card="handleAddCardFromPanel"
                            />
                        </template>

                        <template v-if="select == 'wpp-email'">
                            <EditorWppEmailPanel
                                :site-setting="site_setting"
                                :loading-save="loadingSave"
                                :initial-rotation-angle="rotationAngle"
                                @update:wpp-email="updateWppEmail"
                                @save-settings="saveSettings"
                                @rotate-image="updateRotationAngle"
                            />
                        </template>

                    </div>
                </v-navigation-drawer>

                <HarmonyPublic
                    v-bind="publicProps"
                    @edit-logo="select = 'logo'; menu_editor = true"
                    @edit-title="select = 'title'; menu_editor = true"
                    @edit-description="select = 'description'; menu_editor = true"
                    @edit-card="handleEditCard"
                    @edit-socials="select = 'socials'; menu_editor = true"
                    @edit-wpp-email="select = 'wpp-email'; menu_editor = true"
                    @add-card="select = 'add-card'; menu_editor = true"
                    @navigate-to-card="handleNavigateToCard"
                    @like="likeSite"
                    @follow="followHarmony"
                    @show-login="handleShowLogin"
                    @login-success="handleLoginSuccess"
                    @register-success="handleRegisterSuccess"
                >
                    <template #socials-extra>
                        <BurbleAlert v-if="hasSocialsError()" :message="getSocialsError().error" :type="getSocialsError().name"
                            :type_error="getSocialsError().type" :cardNumber="0" position="left" />
                    </template>
                </HarmonyPublic>
            </div>
        </div>
    </div>
</template>

<script>
import axios from '@/axios.js';
import { SOCIAL_BASE_URLS, SOCIAL_COLORS } from '@/config/harmony/socials';
import socialUrlFormatter from '@/mixins/harmony/socialUrlFormatter';
import layoutMixin from '@/mixins/harmony/layoutMixin';
import descriptionMixin from '@/mixins/harmony/descriptionMixin';
import socialMixin from '@/mixins/harmony/socialMixin';
import adultMixin from '@/mixins/harmony/adultMixin';
import analyticsMixin from '@/mixins/harmony/analyticsMixin';
import backgroundMixin from '@/mixins/harmony/backgroundMixin';
import mediaMixin from '@/mixins/harmony/mediaMixin';
import { PATTERNS } from '@/config/harmony/patterns';
import { getPhoneCodesCached, getPlatformsCached } from './harmonyLoader';
import '@/components/harmony/Editor/editor-tokens.css';
import '@/components/harmony/Editor/editor-select.css';
export default {
    name: 'HarmonyEditor',
    mixins: [socialUrlFormatter, layoutMixin, descriptionMixin, socialMixin, adultMixin, analyticsMixin, backgroundMixin, mediaMixin],
    components: {
        BurbleAlert: () => import('@/components/harmony/BurbleAlert.vue'),
        EditorNavbar: () => import('@/components/harmony/EditorNavbar.vue'),
        HarmonyLoader: () => import('@/components/harmony/HarmonyLoader.vue'),
        EditorLogoPanel: () => import('@/components/harmony/Editor/EditorLogoPanel.vue'),
        EditorTitlePanel: () => import('@/components/harmony/Editor/EditorTitlePanel.vue'),
        EditorDescriptionPanel: () => import('@/components/harmony/Editor/EditorDescriptionPanel.vue'),
        EditorSocialsPanel: () => import('@/components/harmony/Editor/EditorSocialsPanel.vue'),
        EditorAddCardPanel: () => import('@/components/harmony/Editor/EditorAddCardPanel.vue'),
        EditorWppEmailPanel: () => import('@/components/harmony/Editor/EditorWppEmailPanel.vue'),
        EditorCardPanel: () => import('@/components/harmony/Editor/EditorCardPanel.vue'),
        HarmonyPublic: () => import('./HarmonyPublic.vue'),
    },
    data() {
        return {
            autoSaveTimer: null,
            resizeTimer: null,
            selectedLayout: 'grid', // 'grid' o 'list'
            turnstileTokenPromise: null,
            widgetId: null,
            harmony: {},
            showPicker: false,
            editor: true,
            menu_editor: false,
            user: null,
            select: '',
            loading: true,
            settings: null,
            colorPlatform: 1,
            cardSelect: 0,
            selectBackground: 1,
            iframeWidth: 568,
            selectColorGradient: {
                a: '',
                b: '',
                direction: ''
            },
            selectColorSolid: '',
            selectNumberGradient: 1,
            selectSocial: 'instagram',
            backend_url: 'https://backend.harmony.fan/storage',
            formattedDescription: '',
            site_setting: {
                profile: {
                    title_color: '',
                    desc_color: '',
                    description: '',
                    title_font: '',
                    shape: '',
                    description_style: {
                        formats: [],
                        alignment: 'center'
                    }
                },
                page: {
                    color: '',
                    background: '',
                    pattern: '',
                },
                wppOrEmail: {
                    textForCard: '',
                    background: {
                        color1: '',
                        color2: '',
                        direction: ''
                    },
                    text: {
                        color: '',
                    }
                },
                style_socials: 1,
                backgrounds: {
                    card1: { path: '', show: true },
                    card2: { path: '', show: true },
                    card3: { path: '', show: true },
                    card4: { path: '', show: true },
                    card5: { path: '', show: true },
                    card6: { path: '', show: true },
                    card7: { path: '', show: true },
                    card8: { path: '', show: true },
                    card9: { path: '', show: true },
                },
                galleries: {
                    card1: { id: null, images: [] },
                    card2: { id: null, images: [] },
                    card3: { id: null, images: [] },
                    card4: { id: null, images: [] },
                    card5: { id: null, images: [] },
                    card6: { id: null, images: [] },
                    card7: { id: null, images: [] },
                    card8: { id: null, images: [] },
                    card9: { id: null, images: [] }
                },
                platforms: {
                    card1: { name: '', nick: '', color: 1, url: '', url2: '', show: true, text: '', textColor: '', fonts: '', size: 'medium', position: 'left', logo_position: 'center' },
                    card2: { name: '', nick: '', color: 1, url: '', url2: '', show: true, text: '', textColor: '', fonts: '', size: 'medium', position: 'left', logo_position: 'center' },
                    card3: { name: '', nick: '', color: 1, url: '', url2: '', show: true, text: '', textColor: '', fonts: '', size: 'large', position: 'left', logo_position: 'center' },
                    card4: { name: '', nick: '', color: 1, url: '', url2: '', show: true, text: '', textColor: '', fonts: '', size: 'medium', position: 'left', logo_position: 'center' },
                    card5: { name: '', nick: '', color: 1, url: '', url2: '', show: true, text: '', textColor: '', fonts: '', size: 'large', position: 'right', logo_position: 'center' },
                    card6: { name: '', nick: '', color: 1, url: '', url2: '', show: true, text: '', textColor: '', fonts: '', size: 'small', position: 'right', logo_position: 'center' },
                    card7: { name: '', nick: '', color: 1, url: '', url2: '', show: true, text: '', textColor: '', fonts: '', size: 'medium', position: 'right', logo_position: 'center' },
                    card8: { name: '', nick: '', color: 1, url: '', url2: '', show: true, text: '', textColor: '', fonts: '', size: 'medium', position: 'right', logo_position: 'center' },
                    card9: { name: '', nick: '', color: 1, url: '', url2: '', show: true, text: '', textColor: '', fonts: '', size: 'small', position: 'right', logo_position: 'center' },
                },
                videos: {
                    card1: { path: '', show: true },
                    card2: { path: '', show: true },
                    card3: { path: '', show: true },
                    card4: { path: '', show: true },
                    card5: { path: '', show: true },
                    card6: { path: '', show: true },
                    card7: { path: '', show: true },
                    card8: { path: '', show: true },
                    card9: { path: '', show: true },
                },
                newplatforms: [],
                schedules: {
                    card1: { background: '', numSection: '', schedule: [] },
                    card2: { background: '', numSection: '', schedule: [] },
                    card3: { background: '', numSection: '', schedule: [] },
                    card4: { background: '', numSection: '', schedule: [] },
                    card5: { background: '', numSection: '', schedule: [] },
                    card6: { background: '', numSection: '', schedule: [] },
                    card7: { background: '', numSection: '', schedule: [] },
                    card8: { background: '', numSection: '', schedule: [] },
                    card9: { background: '', numSection: '', schedule: [] }
                },
            },
            platforms_temp: {
                card1: { name: '', nick: '', url: '', url2: '', show: true },
                card2: { name: '', nick: '', url: '', url2: '', show: true },
                card3: { name: '', nick: '', url: '', url2: '', show: true },
                card4: { name: '', nick: '', url: '', url2: '', show: true },
                card5: { name: '', nick: '', url: '', url2: '', show: true },
                card6: { name: '', nick: '', url: '', url2: '', show: true },
                card7: { name: '', nick: '', url: '', url2: '', show: true },
                card8: { name: '', nick: '', url: '', url2: '', show: true },
                card9: { name: '', nick: '', url: '', url2: '', show: true },
            },
            platformSearch: '',
            platforms: [],
            platformNew: {
                name: '',
            },
            image_or_video: 1,
            selectNewPlatform: '',
            showPlatforms: false,
            showNick: false,
            showUrl: false,
            changes: {},
            socials: [],
            list_socials: [],
            style_socials_temp: null,
            is_user_adult: false,
            rules: {
                noUrl: value => {
                    if (!value) return true;
                    const urlPattern = /^[a-zA-Z0-9._@-]+$/;
                    return urlPattern.test(value) || this.$t('harmony.invalid-nick');
                },
                containUrl: value => {
                    this._containUrlCache = this._containUrlCache || {};
                    if (this._containUrlCache[value] !== undefined) return this._containUrlCache[value];

                    if (!value) {
                        this._containUrlCache[value] = true;
                        return true;
                    }
                    const urlRegex = RegExp(/(https?:\/\/[^\s]+|www\.[^\s]+|\b(?:[a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(?:\/[^\s]*)?\b|(?<![a-zA-Z])(?:\+?\d{1,3})?[-.\s]?\(?\d{1,4}\)?[-.\s]?\d{1,4}[-.\s]?\d{1,9}\b(?!\d)(?=\s|$)|(?<!\d)(?=.*[a-zA-Z])\S*\d{5,}\S*)/g);
                    const result = !urlRegex.test(value) || this.$t('harmony.contains-url');

                    this._containUrlCache[value] = result;
                    return result;
                },
                noUrlPlatform: value => {
                    if (!value) return true;
                    const urlPattern = /^[a-zA-Z0-9._@]+$/;
                    return urlPattern.test(value) || this.$t('harmony.invalid-platform');
                },
                required: value => !!value || this.$t('navbar.required'),
                min: v => v.length >= 8 || this.$t('navbar.min'),
                maxDesc: v => v.length <= 200 || this.$t('panelHome.data-maxDesc200'),
                minPhone: v => v.length >= 6 || this.$t('navbar.min-phone'),
                maxPhone: v => v.length <= 12 || this.$t('navbar.max-phone'),
                numericPhone: v => /^[0-9]+$/.test(v) || this.$t('navbar.numeric-phone'),
                passwordMatch: value => value === this.password || this.$t('navbar.password-match'),
                passwordMatch2: value => value === this.newPassword || this.$t('navbar.password-match'),
                alpha: v => /^[a-zA-ZÑñ ]*$/.test(v) || this.$t('navbar.alpha'),
                minName: v => v.length >= 3 || this.$t('navbar.min-name'),
                maxName: v => v.length <= 30 || this.$t('navbar.max-name'),
                email: value => {
                    const pattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
                    return pattern.test(value) || this.$t('navbar.invalid-email')
                },
            },
            selectedSocial: null,
            socialsBar: ['instagram', 'facebook', 'x', 'tiktok'],
            defaultSocials: [
                {
                    name: 'instagram',
                    nick: ''
                },
                {
                    name: 'facebook',
                    nick: ''
                },
                {
                    name: 'tiktok',
                    nick: ''
                }
            ],
            newSocial: {
                name: '',
                nick: ''
            },
            dialog: false,
            showUrl2: false,
            loadingSave: false,
            isModalOpen: false,
            selectedMediaSrc: '',
            selectedMediaType: 'image',
            itemsForUpload: [],
            notShowField: false,
            galleryUpdated: false,
            position: '',
            size: 'small',
            positions: [
                {
                    name: 'Izquierda',
                    value: 'left'
                },
                {
                    name: 'Derecha',
                    value: 'right'
                }
            ],
            textOrBackgroundItems: [
                {
                    name: 'Texto',
                    value: 'text'
                },
                {
                    name: 'Fondo',
                    value: 'background'
                }
            ],
            selectGradientColorWpp: {
                a: '',
                b: '',
                direction: ''
            },
            colorTextWpp: '',
            textOrBackground: 'text',
            selectGradientWpp: 1,
            saving: false,
            visits: 0,
            dialogEmoticons: false,
            input: '',
            search: '',
            showSchedules: false,
            selectedColorId: null,
            colors: [
                { hex: '#8e84c0' },
                { hex: '#4285bb' },
                { hex: '#34989b' },
                { hex: '#ffca00' },
                { hex: '#ff4e4e' },
                { hex: '#ff8c40' },
                { hex: '#4542a7' },
                { hex: '#ed5bcc' },
                { hex: '#246bef' },
                { hex: '#2cad2c' },
                { hex: '#4faaff' },
                { hex: '#7d0335' },
                { hex: '#333333' },
                { hex: '#790000' }
            ],
            showDetailsPlatform: true,
            showEdit: false,
            showAddSchedule: false,
            isFormValid: false,
            dialogText: false,
            colorOptions: [
                { text: 'Blanco', value: '#FFFFFF' },
                { text: 'Negro', value: '#000000' }
            ],
            tempText: '',
            tempColorText: '',
            rotationAngle: 90,
            directions: this.setDirection(),
            modalAddStyleColor: false,
            styleSocial: {
                color: '000',
                border: 12
            },
            currentIndex: 0,
            dotStyle: {
                left: '0px',
                top: '0px',
            },
            fontsOptions: this.setFonts('AaBbCcDdEeFfGgHhIiJjKkLlMmNnOoPpQqRr...'),
            fontClass: '',
            fontWeight: '',
            fontSize: '',
            harmonyRules: [
                v => !!v || this.$t('panelHome.data-required'),
                v => v.length >= 3 || this.$t('panelHome.data-minChar'),
                v => v.length <= 20 || this.$t('panelHome.data-maxChar'),
                v => /^(?!.*[.]{2})(?!.*[_]{2})(?!.*[ ]{2})[a-zA-Z0-9._ ]*[a-zA-Z0-9]$/.test(v) || this.$t('panelHome.data-validChars'),
            ],
            rulesWpp: {
                required: value => !!value || this.$t('navbar.required'),
                minPhone: v => v.length >= 6 || this.$t('navbar.min-phone'),
                maxPhone: v => v.length <= 12 || this.$t('navbar.max-phone'),
                numericPhone: v => /^[0-9]+$/.test(v) || this.$t('navbar.numeric-phone'),
                email: value => {
                    const pattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
                    return pattern.test(value) || this.$t('navbar.invalid-email')
                }
            },
            platformsAdult: [],
            dialogTitle: false,
            titleWeight: '',
            titleSize: '',
            titleFont: '',
            loadingWpp: false,
            selectedOptionWpp: '',
            phone_codes: null,
            phone_code: '(+57)',
            phone_number: '',
            email: '',
            translatedText: null,
            translatedSelectText: null,
            translatedDescription: null,
            wppOrEmail: '',
            likes: 0,
            isLiked: false,
            isFollowed: false,
            login: false,
            loginLoading: false,
            loginUser: '',
            show1: false,
            first_name: '',
            last_name: '',
            password: '',
            password_confirm: '',
            register: false,
            registerLoading: false,
            userType: [],
            loadingUserType: false,
            role_id: 3,
            translatedFooterText: null,
            isOwner: false,
            activityLog: '',
            colorsNetworks: SOCIAL_COLORS,
            showBackgroundPage: false,
            showPhotoProfile: false,
            monetize: false,
            imageExtensions: null,
            videoExtensions: null,
            socialBaseUrls: SOCIAL_BASE_URLS,
            incomplete_configurations: [],
            patterns: PATTERNS,
            textFormats: [],
            textAlignment: 'center',
        };
    },
    metaInfo() {
        const defaultImage = 'https://backend.harmony.fan/storage/images/harmony.png';
        return {
            titleTemplate: '%s - Harmony Fan',
            title: this.harmony.name || 'Harmony Fan',
            meta: [
                { charset: 'utf-8' },
                { name: 'viewport', content: 'width=device-width, initial-scale=1' },
                { name: 'description', content: this.harmony.description || 'Harmony fan es una herramienta que te permite con un solo Clic conectar tu audiencia con tus plataformas y ganar dinero al hacerlo.' },
                { property: 'og:title', content: this.harmony.name || 'Harmony Fan' },
                { property: 'og:description', content: this.harmony.description || 'Harmony fan es una herramienta que te permite con un solo Clic conectar tu audiencia con tus plataformas y ganar dinero al hacerlo.' },
                { property: 'og:image', content: this.harmony.logo_path || defaultImage },
                { property: 'og:url', content: `https://harmony.fan/${this.harmony.slug}` },
                { name: 'twitter:title', content: this.harmony.name || 'Harmony Fan' },
                { name: 'twitter:description', content: this.harmony.description || 'Harmony fan es una herramienta que te permite con un solo Clic conectar tu audiencia con tus plataformas y ganar dinero al hacerlo.' },
                { name: 'twitter:image', content: this.harmony.logo_path || defaultImage },
                { name: 'twitter:card', content: 'summary_large_image' }
            ],
        };
    },
    watch: {
        "$i18n.locale": function () {
        },
        showSchedule(newValue) {
            if (!newValue) {
                this.onAddScheduleClosed();
            }
        },
        selectBackground(newValue) {
            if (newValue === 1) {
                this.updateGradient();
            }
        },
    },
    mounted() {
        this.turnstileTokenPromise = this.getTokenCloudflare();
        window.addEventListener('resize', this.handleResize);
    },
    beforeDestroy() {
        if (this.autoSaveTimer) {
            clearTimeout(this.autoSaveTimer);
        }
        if (this.resizeTimer) {
            clearTimeout(this.resizeTimer);
        }
        window.removeEventListener('resize', this.handleResize);
    },

    computed: {
        sizes() {
            const baseSizes = [
                {
                    name: 'Pequeño',
                    value: 'small'
                },
                {
                    name: 'Mediano',
                    value: 'medium'
                },
                {
                    name: 'Grande',
                    value: 'large'
                }
            ];

            if (this.selectedLayout === 'list') {
                baseSizes.push({
                    name: 'Ancho completo',
                    value: 'full-width'
                });
            }

            return baseSizes;
        },
        shapeClass() {
            const s = this.site_setting.profile.shape;
            if (s === 'rectangle') return 'logo-rectangle';
            if (s === 'circle') return 'rounded-circle';
            return 'rounded-xl';
        },

        isValidEmail() {
            return (
                this.rulesWpp.required(this.email) === true &&
                this.rulesWpp.email(this.email) === true
            );
        },

        isValidWpp() {
            return (
                this.rulesWpp.required(this.phone_code) === true &&
                this.rulesWpp.required(this.phone_number) === true &&
                this.rulesWpp.numericPhone(this.phone_number) === true &&
                this.rulesWpp.minPhone(this.phone_number) === true &&
                this.rulesWpp.maxPhone(this.phone_number) === true
            );
        },
        colorWppComputed: {
            get() {
                return this.textOrBackground === 'text' ? this.colorTextWpp : this.selectGradientColorWpp.a;
            },
            set(value) {
                if (this.textOrBackground === 'text') {
                    this.colorTextWpp = value;
                } else {
                    this.$set(this.selectGradientColorWpp, 'a', value);
                }
            }
        },
        filteredSocials() {
            return this.socialsBar.filter((social, index) => index !== 1);
        },
        filteredPlatforms() {
            return this.platforms.filter(platform =>
                platform.name.toLowerCase().includes(this.platformSearch.toLowerCase())
            );
        },
        hasExactMatch() {
            if (!this.platformSearch) return false;
            return this.platforms.some(platform =>
                platform.name.toLowerCase() === this.platformSearch.toLowerCase()
            );
        },
        selectedCard() {
            return this.site_setting.platforms[`card${this.cardSelect}`];
        },

        selectedNewPlatform() {
            return this.site_setting.newplatforms.find(platform => platform.name === this.selectNewPlatform);
        },

        defaultImageOrVideo() {
            const hasImage = !!this.site_setting.backgrounds[`card${this.cardSelect}`]?.path;
            const hasVideo = !!this.site_setting.videos[`card${this.cardSelect}`]?.path;
            const hasGallery = this.hasImages(this.site_setting.galleries[`card${this.cardSelect}`]);
            const hasSchedule = this.hasSchedule(this.site_setting.schedules[`card${this.cardSelect}`]);
            const hasNumSection = !!(this.site_setting.schedules[`card${this.cardSelect}`]?.numSection);
            const hasBackground = !!(this.site_setting.schedules[`card${this.cardSelect}`]?.background);

            if (hasImage && !hasNumSection && !hasSchedule) {
                return 1;
            }
            else if (hasVideo) {
                return 2;
            }
            else if (hasGallery) {
                return 3;
            }
            else if (hasSchedule || hasNumSection || hasBackground) {
                return 4;
            }
            return 1;
        },


        isDisabled() {
            const cardKey = `card${this.cardSelect}`;
            const schedule = this.site_setting.schedules[cardKey];
            const hasSchedule = this.hasSchedule(schedule);
            const hasNumSection = !!(schedule?.numSection);
            const hasBackground = !!(this.site_setting.schedules[`card${this.cardSelect}`]?.background);

            if (this.image_or_video === 4) {
                return hasSchedule || hasNumSection || hasBackground;
            } else if (this.image_or_video === 2) {
                return !!this.site_setting.videos[cardKey].path;
            } else if (this.image_or_video === 3) {
                return this.hasImages(this.site_setting.galleries[cardKey]);
            } else if (this.image_or_video === 1) {
                return !!this.site_setting.backgrounds[cardKey].path;
            }
            return false;
        },

        maxHeight() {
            const viewportHeight = window.innerHeight;
            const maxAllowed = Math.min(viewportHeight * 0.85, 800);
            return maxAllowed + 'px';
        },


        valid() {
            if (this.selectedCard === undefined) {
                return false;
            }
            return this.rules.noUrl(this.selectedCard.nick) === true;
        },
        validPlatform() {
            return this.rules.noUrlPlatform(this.platformSearch) === true;
        },

        validNick() {
            return this.rules.required(this.newSocial.nick) === true;
        },

        validText() {
            const textToValidate = this.tempText || this.selectedCard.text;
            return this.rules.required(textToValidate) === true;
        },

        socialItems() {
            return this.countSocials() > 0 ? this.socials : this.defaultSocials;
        },
        buttonContainerStyle() {
            return {
                height: (!this.showUrl2 && !this.showUrl && !this.showNick && this.showPlatforms) ? '90px' : '40px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
            };
        },

        showPlatformCard: {
            get() {
                return this.site_setting.platforms[`card${this.cardSelect}`].show;
            },
            set(value) {
                this.site_setting.platforms[`card${this.cardSelect}`].show = value;
            }
        },
        shouldShowWppEmail() {
            const card5 = this.site_setting?.platforms?.card5;
            return (card5?.position === 'right') || this.editor;
        },
        showSocials() {
            if (this.editor) {
                return true;
            }
            return this.socialItems.some(social => social.nick && social.nick.trim() !== '');
        },
        text: {
            get() {
                return this.tempText || this.selectedCard.text;
            },
            set(value) {
                this.tempText = value;
            }
        },
        color: {
            get() {
                return this.tempColorText || this.selectedCard.textColor;
            },

            set(value) {
                this.tempColorText = value;
            }
        },
        colorPersonalized: {
            get() {
                return this.styleSocial.color ? `#${this.styleSocial.color}` : '';
            },
            set(value) {
                this.styleSocial.color = value.replace('#', '');
            }
        },
        currentImage() {
            return `https://backend.harmony.fan/storage/socials/no-styles/${this.list_socials[this.currentIndex].value}.png`;
        },
        font: {
            get() {
                return this.fontClass || this.selectedCard.fonts.font;
            },
            set(value) {
                this.fontClass = value
            }
        },
        weight: {
            get() {
                return this.fontWeight || this.selectedCard.fonts.weight;
            },
            set(value) {
                this.fontWeight = value
            }
        },
        sizeFont: {
            get() {
                return this.fontSize || this.selectedCard.fonts.size;
            },
            set(value) {
                this.fontSize = value;
            }
        },
        isValidHarmonyName() {
            return this.harmonyRules.every(rule => rule(this.harmony.name) === true);
        },
        isDescriptionValid() {
            if (this.rules.containUrl(this.site_setting.profile.description) == true) {
                return true
            }
            return false
        },
        isFormValidLogin() {
            return (
                this.rules.required(this.email) === true &&
                this.rules.email(this.email) === true &&
                this.rules.required(this.password) === true &&
                this.rules.min(this.password) === true
            );
        },
        isFormValidRegister() {
            return (
                this.rules.required(this.first_name) === true &&
                this.rules.alpha(this.first_name) === true &&
                this.rules.minName(this.first_name) === true &&
                this.rules.maxName(this.first_name) === true &&
                this.rules.required(this.last_name) === true &&
                this.rules.alpha(this.last_name) === true &&
                this.rules.minName(this.last_name) === true &&
                this.rules.maxName(this.last_name) === true &&
                this.rules.required(this.email) === true &&
                this.rules.email(this.email) === true &&
                this.rules.required(this.phone_code) === true &&
                this.rules.required(this.phone_number) === true &&
                this.rules.numericPhone(this.phone_number) === true &&
                this.rules.minPhone(this.phone_number) === true &&
                this.rules.maxPhone(this.phone_number) === true &&
                this.rules.required(this.password) === true &&
                this.rules.min(this.password) === true &&
                this.rules.required(this.password_confirm) === true &&
                this.rules.min(this.password_confirm) === true &&
                this.rules.passwordMatch(this.password_confirm) === true
            );
        },
        publicProps() {
            return {
                editor: this.editor,
                site_setting: this.site_setting,
                harmony: this.harmony,
                socials: this.socials,
                monetize: this.monetize,
                isOwner: this.isOwner,
                likes: this.likes,
                isLiked: this.isLiked,
                isFollowed: this.isFollowed,
                visits: this.visits,
                isModalOpen: this.isModalOpen,
                selectedMediaSrc: this.selectedMediaSrc,
                selectedMediaType: this.selectedMediaType,
                translatedText: this.translatedText,
                translatedSelectText: this.translatedSelectText,
                translatedDescription: this.translatedDescription,
                translatedFooterText: this.translatedFooterText,
                loading: this.loading,
                selectedLayout: this.selectedLayout,
                backendUrl: this.backend_url,
                incompleteConf: this.incomplete_configurations,
                styleSocial: this.styleSocial,
                colorTextWpp: this.colorTextWpp,
            };
        },
    },

    async created() {
        this.updateIframeWidth();

        const url = window.location.href;
        const slugMatch = url.match(/^https?:\/\/[^/]+\/([^/?#]+)/);
        const preSlug = slugMatch ? slugMatch[1] : null;
        const slug = preSlug ? preSlug : this.$route.params.slug;

        this.setLanguage();
        this.setSelectList();

        try {
            await Promise.all([
                this.getPlatforms(),
                this.getPhoneCodes(),
                this.getPlatformsAdult(),
                this.loadHarmony(slug)
            ]);
        } catch (error) {
            console.error('Error initializing editor:', error);
        }

        this.loading = false;
    },

    methods: {
        handleResize() {
            if (this.resizeTimer) {
                clearTimeout(this.resizeTimer);
            }
            this.resizeTimer = setTimeout(() => {
                this.updateIframeWidth();
            }, 100);
        },
        updateIframeWidth() {
            const windowWidth = window.innerWidth;

            if (windowWidth <= 768) {
                this.iframeWidth = 300;
            } else {
                this.iframeWidth = 568;
            }
        },


        handleEditCard({ cardNumber, platformName }) {
            this.menu_editor = true;
            this.select = 'card';
            this.cardSelect = cardNumber;
            this.selectPlatformCard(platformName);
        },

        handleNavigateToCard({ card, src, schedule }) {
            this.navigateToCard(card, src, schedule);
        },

        handleAddCard(position) {
            this.position = position;
            this.menu_editor = true;
            this.select = 'add-card';
        },


        async getTokenCloudflare() {
            return new Promise((resolve, reject) => {
                const init = () => {
                    this.widgetId = window.turnstile.render(
                        this.$refs.turnstile,
                        {
                            sitekey: '0x4AAAAAABbWOJ_t8X7HYmoE',
                            callback: resolve,
                            'error-callback': reject
                        }
                    )
                    window.turnstile.execute(this.widgetId)
                }
                if (window.turnstile) return init()
                const url = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
                document.querySelector(`script[src^="${url}"]`)
                    .addEventListener('load', init)
            })
        },

        async getPhoneCodes() {
            try {
                this.phone_codes = await getPhoneCodesCached();
            } catch (error) {
                console.error(error);
            }
        },
        async sendMessage(type) {
            let data;
            if (type == 'WhatsApp') {
                data = {
                    phone_code: this.phone_code,
                    phone_number: this.phone_number,
                    site_id: this.harmony.id,
                    type: type
                };
            } else {
                data = {
                    email: this.email,
                    site_id: this.harmony.id,
                    type: type
                };
            }

            this.loadingWpp = true;
            const response = await axios.post('send-wpp-email', data);
            if (response.data.success) {
                this.loadingWpp = false;
                this.phone_number = '';
                this.email = '';
                this.selectedOptionWpp = '';
                this.$vs.notify({
                    title: this.$t('harmony.wpp-email-sent-title'),
                    color: 'success',
                    icon: 'check_box',
                    position: 'bottom-center',
                    time: 5000,
                });
            }
        },
        setSelectList() {
            this.itemsForUpload = [
                {
                    name: this.$t('harmony.image'),
                    id: 1,
                },
                {
                    name: this.$t('harmony.video'),
                    id: 2,
                },
                {
                    name: this.$t('harmony.gallery'),
                    id: 3,
                },
                {
                    name: this.$t('harmony.schedule'),
                    id: 4,
                }
            ]
        },
        openHome() {
            window.open('/', '_blank')
        },

        setLanguage() {
            const storedLocale = localStorage.getItem('locale');
            if (storedLocale) {
                this.$i18n.locale = storedLocale;
            } else {
                this.$i18n.locale = 'es'
            }
        },

        checkPageColor() {
            if (this.site_setting.page.color == '#D4D4D4' && !this.site_setting.page.background || this.site_setting.page.color == '#FFFFFF' && !this.site_setting.page.background) {
                return false
            } else {
                return true
            }
        },

        handleInput(key) {
            this.changes[key] = true;
        },

        async getPlatforms() {
            try {
                this.platforms = await getPlatformsCached();
            } catch (error) {
                console.error(error);
            }
        },

        addPlatform() {
            const newPlatform = { ...this.platformNew };
            newPlatform.name = this.platformSearch;

            const existingPlatform = this.platforms.find(platform => platform.name === newPlatform.name);

            if (!existingPlatform) {
                const basePlatform = {
                    name: newPlatform.name,
                    color: this.colorPlatform,
                };

                this.platforms.push(basePlatform);

                const existingInNew = this.site_setting.newplatforms.find(p => p.name === newPlatform.name);
                if (!existingInNew) {
                    this.site_setting.newplatforms.push({
                        name: basePlatform.name,
                        color: basePlatform.color
                    });
                }

                this.selectPlatform(newPlatform.name);
            }
            this.platformNew = { name: '' };
        },

        getPlatformImage(platform) {
            if (this.colorPlatform === 1) {
                return platform.srcBlack;
            } else if (this.colorPlatform === 2) {
                return platform.srcWhite;
            } else {
                return platform.srcColor;
            }
        },

        ensureHttps(url) {
            if (url.startsWith('http://') || url.startsWith('https://')) {
                return url;
            }
            return 'https://' + url;
        },

        getTitle() {
            if (this.editor) {
                return 'cursor'
            } else {
                return ''
            }
        },

        isValidWebUrl(url) {
            let regEx = /^(http|https)/;
            return regEx.test(url);
        },


        removePlatform() {
            const cardKey = `card${this.cardSelect}`;

            this.site_setting.platforms[cardKey].name = '';
            this.site_setting.platforms[cardKey].nick = '';
            this.site_setting.platforms[cardKey].url = '';
            this.site_setting.platforms[cardKey].url2 = '';
            this.site_setting.platforms[cardKey].text = '';
            this.site_setting.platforms[cardKey].textColor = '#000000';
            this.site_setting.platforms[cardKey].fonts.font = '';
            this.site_setting.platforms[cardKey].fonts.weight = '';
            this.site_setting.platforms[cardKey].fonts.size = '';

            this.tempText = '';
            this.tempColorText = '#000000';
            this.fontClass = '';
            this.fontWeight = '';
            this.fontSize = '';
            this.platformSearch = '';

            this.saveSettings();
        },

        extractParam(url, param) {
            if (this.isValidWebUrl(url)) {
                const regex = new RegExp(`${param}=([^&]+)`);
                const matches = url.match(regex);

                if (matches) {
                    return decodeURIComponent(matches[1]);
                } else {
                    return url
                }
            } else {
                return url
            }
        },

        getBgPlatforms(value) {
            if (value == 1) {
                return ''
            } else if (value == 2) {
                return 'bg-platform'
            } else {
                return 'bg-platform2'
            }
        },

        getPlatformName(fullPath) {
            if (fullPath) {
                const parts = fullPath.split('/');
                return parts[0];
            }
            return '';
        },


        selectPlatform(platformName) {
            const selectedPlatform = this.platforms.find(platform => platform.name === platformName);
            this.selectNewPlatform = '';

            if (selectedPlatform) {
                this.showNick = true;
                const cardKey = `card${this.cardSelect}`;
                let src;

                if (this.colorPlatform === 1) {
                    src = selectedPlatform.srcBlack;
                } else if (this.colorPlatform === 2) {
                    src = selectedPlatform.srcWhite;
                } else {
                    src = selectedPlatform.srcColor;
                }

                if (src) {
                    this.showUrl = false;
                    const fileName = src.split('/').pop();
                    const name = selectedPlatform.name.split('/')[0].toUpperCase();

                    this.showUrl2 = ['INSTAGRAM', 'TIKTOK', 'FACEBOOK', 'X', 'YOUTUBE', 'SPOTIFY', 'VIMEO', 'THREADS', 'LINKEDIN', 'PINTEREST', 'REDDIT', 'DISCORD', 'TELEGRAM', 'WHATSAPP', 'AMAZON', 'MANYVIDS', 'KWAI', 'EROME', 'MYDIRTYHOBBY', 'MYCLUB', 'LIVESTRIP', 'LOVENSE', 'PAYPAL', 'REDGITFS', 'MYWISHCAM', 'SAKURALIVE', 'SEX.COM', 'SKYPRIVATE', 'XHAMSTER', 'KWIKY', 'REDGIFS', 'LIVESTRIP', 'LOVENSE', 'FLINBO'].includes(name);

                    this.site_setting.platforms[cardKey].name = `${selectedPlatform.name}/${fileName}`;
                } else {
                    this.selectNewPlatform = selectedPlatform.name;
                    this.showUrl = true;

                    this.site_setting.platforms[cardKey].name = selectedPlatform.name;
                    this.site_setting.platforms[cardKey].color = this.colorPlatform === 1 ? 2 : 1;
                }
            }
        },

        changeColor(color) {
            this.colorPlatform = color;
            const cardKey = `card${this.cardSelect}`;
            const platformName = this.site_setting.platforms[cardKey].name.split('/')[0];
            const selectPlatform = this.platforms.find(platform => platform.name === platformName);

            if (selectPlatform) {
                if (selectPlatform.srcBlack || selectPlatform.srcWhite) {
                    let src;

                    if (this.colorPlatform === 1) {
                        src = selectPlatform.srcBlack;
                    } else if (this.colorPlatform === 2) {
                        src = selectPlatform.srcWhite;
                    } else {
                        src = selectPlatform.srcColor;
                    }

                    const parts = src.split('/');
                    const fileName = parts[parts.length - 1];
                    this.site_setting.platforms[cardKey].name = `${platformName}/${fileName}`;
                } else {
                    if (this.colorPlatform === 1) {
                        this.site_setting.platforms[`card${this.cardSelect}`].color = 2;
                    } else {
                        this.site_setting.platforms[`card${this.cardSelect}`].color = 1;
                    }
                }
            }
        },

        changeLogoPosition(position) {
            this.site_setting.platforms[`card${this.cardSelect}`].logo_position = position;
        },

        selectPlatformCard(platformName) {
            const selectedPlatform = this.platforms.find(platform => platform.name === platformName.split('/')[0]);
            this.selectNewPlatform = '';
            this.showNick = false;
            this.showUrl = false;

            if (selectedPlatform) {
                this.showNick = true;
                const parts = platformName.split('/');
                const afterSlash = parts[1];

                if (selectedPlatform.srcBlack || selectedPlatform.srcWhite) {
                    this.colorPlatform = afterSlash.includes('NEGRO') ? 1 : afterSlash.includes('COLOR') ? 3 : 2;
                    this.showUrl2 = ['INSTAGRAM', 'TIKTOK', 'FACEBOOK', 'X', 'YOUTUBE', 'SPOTIFY', 'VIMEO', 'THREADS', 'LINKEDIN', 'PINTEREST', 'REDDIT', 'DISCORD', 'TELEGRAM', 'WHATSAPP', 'AMAZON', 'MANYVIDS', 'KWAI', 'EROME', 'MYDIRTYHOBBY', 'MYCLUB', 'LIVESTRIP', 'LOVENSE', 'PAYPAL', 'REDGITFS', 'MYWISHCAM', 'SAKURALIVE', 'SEX.COM', 'SKYPRIVATE', 'XHAMSTER', 'KWIKY', 'REDGIFS', 'LIVESTRIP', 'LOVENSE', 'FLINBO'].includes(parts[0]);
                } else {
                    this.selectNewPlatform = selectedPlatform.name;
                    this.site_setting.platforms[`card${this.cardSelect}`].name = selectedPlatform.name;
                    this.showUrl = true;

                    const platformColor = this.site_setting.platforms[`card${this.cardSelect}`].color;
                    this.colorPlatform = platformColor === 2 ? 1 : platformColor === 1 ? 2 : this.colorPlatform;
                }
            }
        },

        getBase(value, data) {
            this._baseCache = this._baseCache || {};
            const cacheKey = `${value}_${data ? (typeof data === 'number' ? data : 'hasData') : 'noData'}_${this.editor ? 'editor' : 'noEditor'}`;
            if (this._baseCache[cacheKey]) return this._baseCache[cacheKey];

            const lookupTable = {
                'socials': {
                    '0': 'base base-socials-editor',
                    'hasData_editor': 'cursor socials base-socials',
                    'hasData_noEditor': 'socials base-socials',
                    'noData_editor': 'cursor socials base-socials',
                    'noData_noEditor': 'socials base-socials'
                },
                'logo': {
                    'editor': 'logo',
                    'noEditor': 'logo2'
                },
                'card': {
                    'hasData_editor': this.editor && !data?.name ? 'base card' : 'base',
                    'hasData_noEditor': 'base2',
                    'noData': 'base2'
                },
                'video': {
                    'hasData_editor': this.editor && !data?.name ? 'video card' : 'base',
                    'hasData_noEditor': 'base',
                    'noData': 'base'
                },
                'card2': {
                    'hasData_editor': this.editor && !data?.name ? 'card2 card' : 'base',
                    'hasData_noEditor': 'base2',
                    'noData': 'base2'
                }
            };

            let result;

            if (value === 'socials') {
                if (data === 0) {
                    result = lookupTable.socials['0'];
                } else {
                    result = lookupTable.socials[`hasData_${this.editor ? 'editor' : 'noEditor'}`];
                }
            } else if (value === 'logo') {
                const hasLogo = this.harmony && this.harmony.logo_path;
                if (this.editor) {
                    result = hasLogo ? 'logo2' : 'logo';
                } else {
                    result = hasLogo ? 'logo2' : 'logo-white';
                }
            } else if (['card', 'video', 'card2'].includes(value)) {
                const dataKey = data ? `hasData_${this.editor ? 'editor' : 'noEditor'}` : 'noData';
                result = lookupTable[value][dataKey] || 'base';
            } else {
                result = 'base';
            }

            this._baseCache[cacheKey] = result;
            return result;
        },

        updateTitle() {
            let formDataEdit = new FormData()
            formDataEdit.append("id", this.harmony.id)
            formDataEdit.append("name_harmony", this.harmony.name)
            try {
                axios.post('/update-title-harmony', formDataEdit, {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                });
            } catch (error) {
                console.error(error);
            }
        },

        async saveSettings() {
            this.loadingSave = true;
            this.saving = true;
            this.updateTitle();
            this.updateTextPlatforms();

            const cleanedNewPlatforms = this.site_setting.newplatforms.map(platform => ({
                name: platform.name,
                color: platform.color || 1,
            }));

            const settingToSave = {
                ...this.site_setting,
                newplatforms: cleanedNewPlatforms
            };

            try {
                await axios.post('/set-site-setting', { site_id: this.harmony.id, setting: JSON.stringify(settingToSave) });

                this.$vs.notify({
                    title: this.$t('harmony.social-configSaved-title'),
                    text: this.$t('harmony.social-configSaved-text'),
                    color: 'success',
                    icon: 'check_box',
                    position: 'bottom-center',
                    time: 5000,
                });

                this.dialogText = false;
                this.loadingSave = false;
            } catch (error) {
                console.error(error);
                this.loadingSave = false;
            }

            this.saving = false;
        },

        saveSettingsAuto() {
            if (this.autoSaveTimer) {
                clearTimeout(this.autoSaveTimer);
            }
            this.autoSaveTimer = setTimeout(async () => {
                await this.executeSaveSettingsAuto();
            }, 500);
        },

        async executeSaveSettingsAuto() {
            if (this.valid && !this.saving && this.harmony?.id) {
                try {
                    for (const social of this.socialItems) {
                        if (this.changes[social.name]) {
                            await axios.post('save-social', { social: social, site_id: this.harmony.id });

                            if (Object.prototype.hasOwnProperty.call(this.changes, social.name)) {
                                this.changes[social.name] = false;
                            }
                        }
                    }

                    const cleanedNewPlatforms = this.site_setting.newplatforms.map(platform => ({
                        name: platform.name,
                        color: platform.color || 1,
                        show: platform.show !== undefined ? platform.show : true
                    }));

                    const settingToSave = {
                        ...this.site_setting,
                        newplatforms: cleanedNewPlatforms
                    };

                    await axios.post('/set-site-setting', { site_id: this.harmony.id, setting: JSON.stringify(settingToSave) });
                } catch (error) {
                    console.error(error);
                }
            }
        },

        checkUser() {
            const storedUser = localStorage.getItem('user');
            this.user = storedUser ? JSON.parse(storedUser) : null;
        },

        async setSettings(value) {
            let changesWereMade = false;

            const defaultSizes = {
                card1: 'medium', card2: 'medium', card3: 'large', card4: 'medium',
                card5: 'large', card6: 'small', card7: 'medium', card8: 'medium', card9: 'small'
            };

            const defaultPositions = {
                card1: 'left', card2: 'left', card3: 'left', card4: 'left',
                card5: 'right', card6: 'right', card7: 'right', card8: 'right', card9: 'right'
            };

            if (!value) {
                if (this.user.id == this.harmony.user_id && this.harmony.status_id != 4) {
                    this.site_setting.profile.title_font = { font: 'font-rubik', weight: '700', size: '32' }
                    this.site_setting.profile.title_color = '#373535'
                    this.site_setting.profile.desc_color = '#373535'
                    this.site_setting.page.color = '#D4D4D4'
                    this.site_setting.page.pattern = 'puntos'
                    this.site_setting.wppOrEmail.background = {
                        direction: 'to top',
                        color1: '#FF00FF00',
                        color2: '#FF00FF00'
                    };
                    this.site_setting.wppOrEmail.text.color = '#000000'
                    this.site_setting.wppOrEmail.textForCard = 'Déjame tu correo o WhatsApp, para enviarte un regalito.'
                    this.site_setting.profile.description = this.descriptionFilter(this.harmony.description)
                    this.selectedSocial = 1
                    this.site_setting.profile.shape = 'square'
                    this.textFormats = [];
                    this.textAligns = 'center';
                    this.selectedLayout = 'grid'
                    this.site_setting.profile.description_style = {
                        formats: [],
                        alignment: 'center'
                    };
                    if (this.harmony.site_settings.layout.name != 'grid') {
                        this.configureListLayout();
                    }
                    await this.saveSettings()
                }
            } else {
                try {
                    const ensureProperty = (obj, prop, defaultValue) => {
                        if (obj[prop] !== undefined) {
                            return true;
                        }

                        this.$set(obj, prop, defaultValue);

                        return obj[prop] !== undefined;
                    };

                    const settings = typeof this.harmony.site_settings?.setting === 'string'
                        ? JSON.parse(this.harmony.site_settings.setting)
                        : (this.harmony.site_settings?.setting || value || {});
                    const profile = settings.profile || {};


                    this.selectedLayout = this.harmony.site_settings.layout.name;
                    this.titleFont = this.site_setting.profile.title_font.font
                    this.titleSize = this.site_setting.profile.title_font.size
                    this.titleWeight = this.site_setting.profile.title_font.weight
                    this.site_setting.profile.title_color = value.profile.title_color;
                    this.site_setting.profile.desc_color = value.profile.desc_color;
                    this.site_setting.profile.description = this.descriptionFilter(this.harmony.description);
                    this.site_setting.page.pattern = value.page.pattern;
                    this.site_setting.page.color = value.page.color;
                    this.selectedSocial = value.style_socials;
                    this.showPhotoProfile = this.harmony.showPhotoProfile;
                    this.incomplete_configurations = this.harmony.incomplete_configurations;

                    if (value.profile && value.profile.description_style) {
                        this.textFormats = value.profile.description_style.formats || [];
                        this.textAlignment = value.profile.description_style.alignment || 'center';
                        this.site_setting.profile.description_style = value.profile.description_style;
                    } else {
                        this.textFormats = [];
                        this.textAligns = 'center';
                        this.site_setting.profile.description_style = {
                            formats: [],
                            alignment: 'center'
                        };
                        changesWereMade = true;
                    }

                    if (value.wppOrEmail && (value.wppOrEmail.background || value.wppOrEmail.text || value.wppOrEmail.textForCard)) {
                        this.site_setting.wppOrEmail.background = value.wppOrEmail.background;
                        this.site_setting.wppOrEmail.text = {
                            ...value.wppOrEmail.text,
                            color: value.wppOrEmail.text.color.slice(0, 7)
                        };

                        this.site_setting.wppOrEmail.textForCard = value.wppOrEmail.textForCard || 'Déjame tu correo o WhatsApp, para enviarte un regalito.';
                        changesWereMade = !value.wppOrEmail.textForCard;
                    } else {
                        this.site_setting.wppOrEmail.background = {
                            direction: 'to top',
                            color1: '#FF00FF00',
                            color2: '#FF00FF00'
                        };
                        this.site_setting.wppOrEmail.text = { color: '#000000' };
                        this.site_setting.wppOrEmail.textForCard = 'Déjame tu correo o WhatsApp, para enviarte un regalito.';
                        changesWereMade = true;
                    }


                    if (value.profile && value.profile.shape) {
                        this.site_setting.profile.shape = value.profile.shape
                    } else {
                        this.site_setting.profile.shape = 'square'
                        changesWereMade = true;
                    }

                    const schedules = this.harmony.site_settings.schedule;
                    const schedulesBG = settings.schedules;
                    Object.keys(this.site_setting.schedules).forEach(card => {
                        this.site_setting.schedules[card].schedule = [];
                        this.site_setting.schedules[card].numSection = '';
                        this.site_setting.schedules[card].background = '';
                    });
                    schedules.forEach(schedule => {
                        const section = schedule.section;
                        ensureProperty(this.site_setting.schedules, section, { background: '', numSection: '', schedule: [] });
                        this.site_setting.schedules[section] = {
                            ...this.site_setting.schedules[section],
                            schedule: schedules
                                .filter(event => event.section === section)
                                .map(event => ({
                                    id: event.id,
                                    name_day: event.name_day,
                                    start_time: event.start_time,
                                    end_time: event.end_time,
                                    url: event.url
                                }))
                        };
                    });

                    if (schedulesBG) {
                        Object.keys(schedulesBG).forEach(cardKey => {
                            const section = [cardKey];
                            ensureProperty(this.site_setting.schedules, section, { background: '', numSection: '', schedule: [] });
                            if (schedulesBG[cardKey].background) {
                                if (schedulesBG[cardKey].background.length) {
                                    this.site_setting.schedules[cardKey].background = schedulesBG[cardKey].background
                                }
                            }
                        });
                    }

                    const backgrounds = this.harmony.site_settings.backgrounds;
                    backgrounds.forEach(background => {
                        if (background.section === 'page-background') {
                            this.site_setting.page.background = background.path;
                            this.showBackgroundPage = background.status !== 3;
                        } else {
                            const section = background.section;
                            ensureProperty(this.site_setting.backgrounds, section, { path: '', show: true });
                            this.site_setting.backgrounds[section].path = background.path;
                            this.site_setting.backgrounds[section].show = background.status !== 3;
                            if (background.section.split('/')[1]) {
                                const numberSection = background.section.split('/')[1];
                                ensureProperty(this.site_setting.schedules, section, { background: '', numSection: '', schedule: [] });
                                this.site_setting.schedules[section].numSection = numberSection;
                            }
                        }
                    });

                    const backgroundsChange = settings.backgrounds;
                    if (backgroundsChange) {
                        changesWereMade = changesWereMade || Object.values(backgroundsChange).some(url => typeof url === 'string');
                    }


                    const videos = this.harmony.site_settings.videos;
                    videos.forEach(video => {
                        const section = video.section;
                        ensureProperty(this.site_setting.videos, section, { path: '', show: true });
                        this.site_setting.videos[section].path = video.path;
                        this.site_setting.videos[section].show = video.status !== 3;
                    });

                    const videosChange = settings.videos;
                    if (videosChange) {
                        changesWereMade = changesWereMade || Object.values(videosChange).some(url => typeof url === 'string');
                    }



                    const platforms = settings.platforms;
                    const existingPlatformNames = new Set(this.platforms.map(platform => platform.name));
                    for (const card in platforms) {
                        if (ensureProperty(platforms, card)) {
                            this.platforms_temp[card] = platforms[card];
                            let temp = {
                                name: platforms[card].name,
                                nick: this.extractParam(platforms[card].nick, 'model'),
                                url: platforms[card].url,
                                url2: platforms[card].url2,
                                color: platforms[card].color || 2,
                            };

                            if (!('show' in platforms[card])) {
                                temp.show = true;
                                changesWereMade = true;
                            } else {
                                temp.show = platforms[card].show;
                            }


                            if (!('text' in platforms[card]) && !('textColor' in platforms[card])) {
                                temp.text = ''
                                temp.textColor = '#000000'
                                changesWereMade = true;
                            } else {
                                temp.text = platforms[card].text
                                temp.textColor = platforms[card].textColor || '#000000'
                            }
                            if (!('fonts' in platforms[card])) {
                                temp.fonts = {
                                    font: 'font-rubik',
                                    weight: '700',
                                    size: '20',
                                }
                            } else {
                                temp.fonts = {
                                    font: platforms[card].fonts.font || 'font-rubik',
                                    weight: platforms[card].fonts.weight || '700',
                                    size: platforms[card].fonts.size || '20',
                                }
                            }


                            if (!('logo_position' in platforms[card])) {
                                temp.logo_position = 'center';
                                changesWereMade = true;
                            } else {
                                temp.logo_position = platforms[card].logo_position;
                            }

                            const cardNumber = parseInt(card.replace('card', ''));

                            if (cardNumber <= 9) {
                                if (!Object.prototype.hasOwnProperty.call(platforms[card], 'size')) {
                                    temp.size = defaultSizes[card];
                                    changesWereMade = true;
                                } else {
                                    temp.size = platforms[card].size;
                                }

                                if (!Object.prototype.hasOwnProperty.call(platforms[card], 'position')) {
                                    temp.position = defaultPositions[card];
                                    changesWereMade = true;
                                } else {
                                    temp.position = platforms[card].position;
                                }
                            }
                            else if (cardNumber >= 10) {
                                temp.position = platforms[card].position;
                                temp.size = platforms[card].size;
                            }


                            this.site_setting.platforms[card] = temp;
                        }
                    }

                    const newPlatforms = settings.newplatforms;

                    newPlatforms.forEach(platform => {
                        if (!existingPlatformNames.has(platform.name)) {
                            if (!('show' in platform)) {
                                platform.show = true;
                                changesWereMade = true;
                            }
                            this.platforms.push(platform);
                            existingPlatformNames.add(platform.name);
                        }
                    });
                    this.site_setting.newplatforms = newPlatforms;

                    this.socials = this.harmony.site_settings.socials;
                    this.site_setting.style_socials = settings.style_socials;
                    this.socials.forEach(social => {
                        this.$set(this.changes, social.name.toLowerCase(), false);
                    });

                    this.newSocial = { name: '', nick: '' };
                    this.style_socials_temp = settings.style_socials;
                    if (this.$route.path.includes('/editor')) {
                        await this.getSocials();
                    }

                    const galleries = this.harmony.site_settings.gallery;
                    galleries.forEach(gallery => {
                        ensureProperty(this.site_setting.galleries, gallery.section, { id: null, images: [] });
                        this.site_setting.galleries[gallery.section] = {
                            id: gallery.id,
                            images: gallery.images.map(image => ({
                                path: image.path,
                                rejected: image.rejected
                            })),
                            explicit: gallery.status !== undefined && gallery.status === 3 ? true : false
                        };
                    });

                    if (!Object.prototype.hasOwnProperty.call(settings, 'galleries')) {
                        settings.galleries = this.site_setting.galleries;
                    }

                    if (!Object.prototype.hasOwnProperty.call(settings, 'schedules')) {
                        settings.schedules = this.site_setting.schedules;
                    }

                    ensureProperty(this.site_setting.profile, 'title_font', { font: '', weight: '', size: '' })

                    if (!('title_font' in profile)) {
                        this.site_setting.profile.title_font = {
                            font: 'font-rubik',
                            weight: '700',
                            size: '32',
                        }
                    } else {
                        this.site_setting.profile.title_font = {
                            font: profile.title_font.font || 'font-rubik',
                            weight: profile.title_font.weight || '700',
                            size: profile.title_font.size || '32',
                        }
                    }

                    for (const cardKey in settings.platforms) {
                        if (Object.prototype.hasOwnProperty.call(settings.platforms, cardKey)) {
                            ensureProperty(this.site_setting.platforms, cardKey, settings.platforms[cardKey]);
                            ensureProperty(this.site_setting.backgrounds, cardKey, settings.backgrounds[cardKey] || { path: '', show: true });
                            ensureProperty(this.site_setting.galleries, cardKey, settings.galleries[cardKey] || { id: null, images: [] });
                            ensureProperty(this.site_setting.videos, cardKey, settings.videos[cardKey] || { path: '', show: true });
                            ensureProperty(this.site_setting.schedules, cardKey, settings.schedules[cardKey] || { background: '', numSection: '', schedule: [] });
                        }
                    }

                    if (changesWereMade && this.harmony?.id && this.$route.path.includes('/editor')) {
                        const cleanedNewPlatforms = this.site_setting.newplatforms.map(platform => ({
                            name: platform.name,
                            color: platform.color || 1,
                        }));
                        const settingToSave = {
                            ...this.site_setting,
                            newplatforms: cleanedNewPlatforms
                        };
                        axios.post('/set-site-setting', { site_id: this.harmony.id, setting: JSON.stringify(settingToSave) }).catch(err => console.error(err));
                    }
                    this.setPickers();
                    this.setPickerWpp();
                } catch (error) {
                    console.error(error);
                }

            }
        },

        async getIncompleteConfigurations() {
            axios.get(`/get-incomplete-configurations/${this.harmony.id}`)
                .then(response => {
                    this.incomplete_configurations = response.data.incompleteConfigurations;
                })
                .catch(error => {
                    console.error('Error fetching incomplete configurations:', error);
                });
        },

        async loadHarmony(slug) {
            let user = JSON.parse(localStorage.getItem('user'));
            if (!user) {
                user = {
                    id: 0,
                };
            }
            this.checkUser();
            try {
                const response = await axios.get(`/site/${slug}`, {
                    params: {
                        user_id: parseInt(user.id)
                    }
                });
                this.harmony = response.data.site;
                this.visits = this.harmony.visits_count;
                this.likes = this.harmony.likes_count;
                this.isLiked = this.harmony.user_has_liked;
                this.isOwner = this.harmony.owner;
                this.isFollowed = this.harmony.user_has_followed;
                this.monetize = response.data.monetize;

                if (this.$route.path.includes('/editor')) {
                    if (!user.id || this.harmony.user_id !== user.id) {
                        const updatedPath = this.$route.path.replace('/editor', '');
                        return this.$router.replace(updatedPath);
                    }
                    this.editor = true;
                }

                if (this.harmony.status_id != 3) {
                    if (this.user && this.user.id == this.harmony.user_id) {
                        // Permitted for owner
                    } else {
                        this.$vs.notify({
                            title: this.$t('harmony.social-errorConnection-title'),
                            text: this.$t('harmony.social-errorConnection-text'),
                            color: '#fcb188',
                            icon: 'warning',
                            position: 'bottom-center',
                            time: 6000,
                        });
                        return this.$router.replace({ name: 'NotFound' });
                    }
                }

                if (this.editor) {
                    await this.getIncompleteConfigurations();
                }
                this.settings = typeof response.data.site.site_settings.setting === 'string'
                    ? JSON.parse(response.data.site.site_settings.setting)
                    : response.data.site.site_settings.setting;
                this.translatedText = response.data.translatedText;
                this.translatedSelectText = response.data.translatedSelectText;
                this.translatedDescription = response.data.translatedDescription;
                this.translatedFooterText = response.data.translatedFooterText;
                await this.setSettings(this.settings);
                return false;

            } catch (error) {
                if (error.message === 'ADULT_REDIRECT') {
                    throw error;
                }
                this.$router.replace({ name: 'NotFound' });
            }
        },

        removeNamePlatform(cardName) {
            this.site_setting.platforms[cardName].name = ''
        },

        hasImages(gallery) {
            return gallery && gallery.images && gallery.images.length > 0;
        },

        addNewImagesGallery() {
            this.galleryUpdated = true
            this.$refs.imageUploadGallery.triggerFileInput()
        },

        async addNewCard() {
            const cardNumber = Object.keys(this.site_setting.platforms).length + 1;
            const newCardKey = `card${cardNumber}`;
            const newCard = { name: '', nick: '', color: 1, url: '', url2: '', show: true, text: '', textColor: '#000000', position: this.position, size: this.size };

            try {
                this.$set(this.site_setting.platforms, newCardKey, newCard);
                this.$set(this.site_setting.backgrounds, newCardKey, { path: '', show: true });
                this.$set(this.site_setting.galleries, newCardKey, { id: null, images: [], });
                this.$set(this.site_setting.videos, newCardKey, { path: '', show: true });
                this.$set(this.site_setting.schedules, newCardKey, { numSection: '', schedule: [], });

                if (this.$route.path.includes('/editor')) {
                    await this.saveSettings();
                }
            } catch (error) {
                console.log(error)
            }
        },
        hasSchedule(card) {
            return card && card.schedule && card.schedule.length > 0;
        },
        hasScheduleNum(card) {
            return card && card.numSection.length > 0;
        },
        selectColor(id) {
            this.selectedColorId = id;
            this.site_setting.schedules[`card${this.cardSelect}`].background = this.selectedColorId;
            this.saveSettings()
        },
        showDetailsPlatformSchedule() {
            this.showDetailsPlatform = !this.showDetailsPlatform;
        },
        checkPlatformSchedule() {
            if (this.image_or_video === 4 && this.site_setting.platforms[`card${this.cardSelect}`].name) {
                this.showDetailsPlatform = true;
            } else {
                this.showDetailsPlatform = true;
            }
        },
        editSchedule() {
            this.showEdit = false;
            this.showAddSchedule = false;
            this.$refs.UploadSchedule.editSchedule();
        },
        saveSchedule() {
            this.showAddSchedule = false;
            this.$refs.UploadSchedule.saveSchedule();
        },
        showBtnSchedule(eventData) {
            this.showAddSchedule = eventData.showAddSchedule;
            this.isFormValid = eventData.enable_or_disabled;

        },
        removeTextPlatform() {
            this.tempText = ''
            this.tempColorText = '#000000'
            this.fontClass = 'font-rubik'
            this.fontSize = '20'
            this.fontWeight = '700'
            this.saveSettings()
            this.$refs.platformText.resetValidation()
        },
        async showAlert() {
            const result = await this.$swal({
                title: `<strong style="font-size: 14px;">${this.$t('harmony.contentExplicit')}</strong>`,
                html: `<p style="font-size: 11px; margin-bottom: 10px;">${this.$t('harmony.contentExplicitDesc')}</p>`,
                width: 326,
                heightAuto: false,
                customClass: {
                    icon: 'notborder',
                    title: 'titleSwal',
                    popup: 'containerSwal',
                    htmlContainer: 'htmlContainerSwal',
                    actions: 'actionsSwal',
                    confirmButton: 'confirmButtonSwal',
                    cancelButton: 'cancelButtonSwal',
                },
                showCancelButton: true,
                confirmButtonText: `<i class="fa fa-thumbs-up"></i> ${this.$t('harmony.showContentExplicit')}`,
                cancelButtonText: `${this.$t('harmony.hideContentExplicit')}`,
                confirmButtonColor: '#35bd5d',
                cancelButtonColor: '#d33',
                background: '#f2f2f2',
                backdrop: `rgba(0, 0, 0, 0.7) no-repeat`,

            });
            return result.isConfirmed;
        },
        setFonts(text) {
            return [
                { text: text, value: 'font-rubik', fontName: 'Rubik' },
                { text: text, value: 'font-anton', fontName: 'Anton' },
                { text: text, value: 'font-bebas-neue', fontName: 'Bebas Neue' },
                { text: text, value: 'font-bree-serif', fontName: 'Bree Serif' },
                { text: text, value: 'font-caveat', fontName: 'Caveat' },
                { text: text, value: 'font-concert-one', fontName: 'Concert One' },
                { text: text, value: 'font-courgette', fontName: 'Courgette' },
                { text: text, value: 'font-creepster', fontName: 'Creepster' },
                { text: text, value: 'font-dancing-script', fontName: 'Dancing Script' },
                { text: text, value: 'font-fira-sans-condensed', fontName: 'Fira Sans Condensed' },
                { text: text, value: 'font-indie-flower', fontName: 'Indie Flower' },
                { text: text, value: 'font-jockey-one', fontName: 'Jockey One' },
                { text: text, value: 'font-orbitron', fontName: 'Orbitron' },
                { text: text, value: 'font-oswald', fontName: 'Oswald' },
                { text: text, value: 'font-pacifico', fontName: 'Pacifico' },
                { text: text, value: 'font-permanent-marker', fontName: 'Permanent Marker' },
                { text: text, value: 'font-pixelify-sans', fontName: 'Pixelify Sans' },
                { text: text, value: 'font-protest-strike', fontName: 'Protest Strike' },
                { text: text, value: 'font-roboto', fontName: 'Roboto' },
                { text: text, value: 'font-teko', fontName: 'Teko' },
                { text: text, value: 'font-titillium-web', fontName: 'Titillium Web' },
            ];
        },
        formatText(item) {
            return `${item.fontName} : ${item.text}`;
        },
        saveNickSettings() {
            this.site_setting.profile.title_font.font = this.titleFont;
            this.site_setting.profile.title_font.size = this.titleSize;
            this.site_setting.profile.title_font.weight = this.titleWeight;
            this.saveSettings();
            this.updateTitle();
        },
        descriptionFilter(text) {
            if (text) {
                const cleanedText = text.replace(/(https?:\/\/[^\s]+|www\.[^\s]+|\b(?:[a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(?:\/[^\s]*)?\b|(?<![a-zA-Z])(?:\+?\d{1,3})?[-.\s]?\(?\d{1,4}\)?[-.\s]?\d{1,4}[-.\s]?\d{1,9}\b(?!\d)(?=\s|$)|(?<!\d)(?=.*[a-zA-Z])\S*\d{5,}\S*)/g, "");
                return cleanedText
            }
            return ''
        },
        async likeSite() {
            const user = JSON.parse(localStorage.getItem('user'));
            if (user) {
                this.isLiked = !this.isLiked
                if (this.isLiked) {
                    this.likes = this.likes + 1
                } else {
                    this.likes = this.likes - 1
                }
                try {
                    await axios.post('/like-site', { site_id: this.harmony.id });
                } catch (error) {
                    if (error.response && error.response.status === 400) {
                        console.log(error.response.data.message);
                    }
                }
            } else {
                this.activityLog = 'like';
                this.login = true
            }
        },

        async followHarmony() {
            const user = JSON.parse(localStorage.getItem('user'));
            if (user) {
                try {
                    this.isFollowed = !this.isFollowed;
                    await axios.post('/follow-site', { site_id: this.harmony.id });
                } catch (error) {
                    if (error.response && error.response.status === 400) {
                        console.log(error.response.data.message);
                    }
                }
            } else {
                this.activityLog = 'follow';
                this.login = true
            }
        },
        updateTextPlatforms() {
            if (this.select == 'card') {
                if (this.selectedCard) {
                    if (this.fontSize > 50) {
                        this.fontSize = 50
                        this.sizeFont = 50
                    }
                    if (this.fontSize < 5) {
                        this.fontSize = 5
                        this.sizeFont = 5
                    }
                }
                this.site_setting.platforms[`card${this.cardSelect}`].text = this.tempText;
                this.site_setting.platforms[`card${this.cardSelect}`].textColor = this.tempColorText;
                this.site_setting.platforms[`card${this.cardSelect}`].fonts.font = this.fontClass;
                this.site_setting.platforms[`card${this.cardSelect}`].fonts.weight = this.fontWeight;
                this.site_setting.platforms[`card${this.cardSelect}`].fonts.size = this.fontSize;

            }
        },
        getSocialColor(socialName) {
            const color = this.colorsNetworks.find(network => network.social === socialName);
            return color ? color.hex : '#000';
        },
        async registerVisit() {
            const FingerprintJS = await import(
                /* webpackChunkName: "fingerprint" */
                '@fingerprintjs/fingerprintjs'
            );

            let token = await this.turnstileTokenPromise;
            let visitorId = localStorage.getItem('visitorId');
            let components = null;

            if (!visitorId) {
                const fpPromise = FingerprintJS.load();
                const fp = await fpPromise;
                const result = await fp.get();
                visitorId = result.visitorId;
                components = result.components;
                localStorage.setItem('visitorId', visitorId);
            } else {
                const fp = await FingerprintJS.load();
                const result = await fp.get();
                components = result.components;
            }

            axios.post('/register-visit', {
                site_id: this.harmony.id,
                user_id: this.harmony.user_id,
                fingerprint: visitorId,
                user_agent: navigator.userAgent,
                language: navigator.language,
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                platform: navigator?.userAgentData?.platform || navigator?.platform,
                screen_resolution: `${screen.width}x${screen.height}`,
                webgl_vendor: components.webGlBasics?.value || null,
                audio_hash: components.audio?.value || null,
                token_cloudflare: token,
            });
        },

        handleLoginSuccess() {
            this.login = false;
            this.loginLoading = false;
            this.loginUser = '';
            this.password = '';
            this.$vs.notify({
                title: this.$t('navbar.login-success'),
                color: 'success',
                icon: 'check_box',
                position: 'bottom-center',
                time: 5000,
            });
        },
        handleRegisterSuccess() {
            this.register = false;
            this.registerLoading = false;
            this.email = '';
            this.password = '';
            this.password_confirm = '';
            this.$vs.notify({
                title: this.$t('navbar.register-success'),
                color: 'success',
                icon: 'check_box',
                position: 'bottom-center',
                time: 5000,
            });
        },
        handleLoginClosed() {
            this.login = false;
            this.loginLoading = false;
            this.loginUser = '';
            this.password = '';
        },

        handleShowLogin(activity) {
            this.activityLog = activity;
            this.login = true;
        },

        updateHarmonyName(name) {
            this.harmony.name = name;
        },
        updateTitleFont(fontObj) {
            this.site_setting.profile.title_font = {
                font: fontObj.font,
                weight: fontObj.weight,
                size: fontObj.size
            };
        },
        updateTitleColor(color) {
            this.site_setting.profile.title_color = color;
        },
        updateDescription(desc) {
            this.site_setting.profile.description = desc;
            this.formattedDescription = this.addLineBreaks(desc);
        },
        updateDescColor(color) {
            this.site_setting.profile.desc_color = color;
        },
        updateDescriptionStyleFromPanel(style) {
            this.site_setting.profile.description_style = style;
            this.textFormats = style.formats;
            this.textAlignment = style.alignment;
        },
        updateGradientFromPanel(gradient) {
            this.selectColorGradient = gradient;
            this.site_setting.page.color = {
                color1: gradient.a,
                color2: gradient.b,
                direction: gradient.direction
            };
        },
        updateRotationAngle(angle) {
            this.rotationAngle = angle;
        },
        updateWppEmail(wppEmail) {
            this.site_setting.wppOrEmail = {
                ...this.site_setting.wppOrEmail,
                ...wppEmail
            };
        },
        handleAddCardFromPanel({ size, position }) {
            this.size = size;
            this.position = position;
            this.addNewCard();
        },
        addPlatformFromPanel(platform) {
            this.platforms.push(platform);
        },
    },

    directives: {
        focus: {
            inserted(el) {
                el.focus()
            },
        },
    }
}
</script>

<style lang="scss">
.theme--dark.v-text-field>.v-input__control>.v-input__slot:before {
    border-color: var(--colorTextWpp);
}

.theme--dark.v-text-field>.v-input__control>.v-input__slot {
    border-color: var(--colorTextWpp);
}

.theme--dark .v-label {
    color: var(--colorTextWpp) !important;
}
</style>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Anton&family=Bebas+Neue&family=Bree+Serif&family=Caveat:wght@400..700&family=Concert+One&family=Courgette&family=Creepster&family=Dancing+Script:wght@400..700&family=Fira+Sans+Condensed:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&family=Indie+Flower&family=Jockey+One&family=Orbitron:wght@400..900&family=Oswald:wght@200..700&family=Pacifico&family=Permanent+Marker&family=Pixelify+Sans:wght@400..700&family=Protest+Strike&family=Roboto:ital,wght@0,100;0,300;0,400;0,500;0,700;0,900;1,100;1,300;1,400;1,500;1,700;1,900&family=Teko:wght@300..700&family=Titillium+Web:ital,wght@0,200;0,300;0,400;0,600;0,700;0,900;1,200;1,300;1,400;1,600;1,700&display=swap');

.ad-container {
    margin-bottom: 10px;
}


.logo-rectangle {
    width: 180px !important;
}


.rounded-circle {
    border-radius: 50% !important;
}

.patternDiv.selected {
    border: 2px solid #000000;
    border-radius: 8px;
}

.close-button {
    position: absolute;
    top: 10px;
    right: 10px;
    z-index: 1;
}

.v-btn ::v-deep .v-input--selection-controls__input {
    margin: 0;
}

.checkbox-on {
    color: #9BA9BD !important;
}

.checkbox-off {
    color: #d3d3d3 !important;
}

.harmony-button {
    position: fixed;
    top: 12px;
    left: 12px;
    z-index: 90;
    cursor: pointer;
    transition: 2s;
}


.icon {
    padding: 5px;
    font-size: 1.2rem;
    transition: transform 0.3s ease;
    color: #fff !important;
}

.margins-icon {
    padding: 6px;
}


.item-section {
    font-size: 20px;
    width: fit-content;
    margin-left: 4px;
}

.section-statistics {
    position: fixed;
    top: 14px;
    width: 100%;
    z-index: 90;
    display: flex;
    justify-content: flex-end;
    align-items: center;
}

.harmony-button img {
    width: 30px;
}

.gallery-component-card {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    z-index: 1000;
}

@media (width <=600px) {
    .harmony-button img {
        width: 30px !important;
    }

    .views {
        font-size: 12px;
        right: 20px;
    }
}

.responsive-text {
    word-wrap: break-word;
    word-break: break-word;
    overflow-wrap: break-word;
    font-size: 1rem;
}

@media (max-width: 354px) {
    .responsive-text {
        font-size: 0.875rem;
        line-height: 1.2;
    }

    .responsive-text v-icon {
        vertical-align: middle;
    }
}

.bg-white-title {
    padding-top: 6px;
    padding-bottom: 4px;
    border-radius: 7px;
    background-color: white;
}

.bg-white {
    padding-top: 6px;
    padding-bottom: 4px;
    border-radius: 7px;
    background-color: white;
}

.card-text-shadow {
    text-shadow: #00000054 0px 0px 5px;
}

.card-shadow-logo {
    box-shadow: #878282 0px 1px 6px 0.1px;
}

.card-shadow-socials {
    box-shadow: #0000002b 0px 1px 10px 1.3px;
}

.card-shadow {
    overflow: hidden !important;
    box-shadow: #00000061 0px 1px 6px 1.3px;
}

.video-background::-webkit-media-controls {
    display: none !important;
}


.icon-redes {
    width: 24px;
    margin: auto;
}

.video-background::-webkit-media-controls-start-playback-button {
    display: none !important;
}

.video-background::-webkit-media-controls-overlay-play-button {
    display: none !important;
}

.video-background::-webkit-media-controls-volume-slider {
    display: none !important;
}

.button-footer {
    background-color: #ffffff;
    font-size: 11px;
    padding-top: 32px !important;
    padding-bottom: 32px !important;
}

@media (min-width: 321px) and (max-width: 400px) {
    .button-footer {
        font-size: 0.6rem;
        padding: 0.4rem;
    }
}

@media (max-width: 320px) and (min-width: 100px) {
    .button-footer {
        font-size: 0.5rem;
        padding: 0.2rem;
    }
}

.a-footer {
    color: inherit;
    text-decoration: none;
}

.patternDiv {
    width: 50px;
    height: 50px;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
}

.patternDiv img.icon {
    width: 24px;
    height: 24px;
}

.newPlatform {
    font-size: 32px;
    font-weight: bold;
    text-align: center;
}

.white h1 {
    color: white !important;
}

.black h1 {
    color: black !important;
}

.cursor {
    cursor: pointer;
}

.floating-drawer {
    position: fixed;
    top: 0;
    right: 0;
    z-index: 1000;
}

.bg-platform {
    background-color: #6b6b6b;
}

.bg-platform2 {
    background-color: #dfdbdb;
}

.card-container {
    position: relative;
    height: 180px;
}

.video-container {
    position: relative;
    height: 503px;
}

.card2-container {
    position: relative;
    height: 130px;
}

.patterns-div {
    height: 85px;
    overflow: auto;
}

.platforms-div {
    height: 104px;
    overflow: auto;
}

.socials-div {
    height: 65px;
    overflow: auto;
}

.selected-box {
    border: 2px solid #8e84c0;
    border-radius: 50%;
    padding: 5px;
    margin-top: 5px;
}

.selected-icon {
    color: #8e84c0;
}

.platforms {
    width: 165px;
}

.base-socials {
    background-color: white;
}

.base-socials-editor {
    cursor: pointer;
    background-color: white;
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 48px;
}

.socials-select {
    max-height: 30px;
}

.socials-icon {
    width: 30px;
}

.socials-icon2 {
    width: 40px;
}

.icon-size-smallest {
    width: 35px !important;
    height: 35px !important;
}

.icon-size-small {
    width: 30px !important;
    height: 30px !important;
}

.icon-size-medium {
    width: 34px !important;
    height: 34px !important;
}

.icon-size-large {
    width: 40px !important;
    height: 40px !important;
}

@media(width >=960px) {
    .icon-size-medium {
        width: 50px !important;
        height: 50px !important;
    }

    .icon-size-small {
        width: 50px !important;
        height: 50px !important;
    }

    .icon-size-large {
        width: 50px !important;
        height: 50px !important;
    }

    .icon-size-smallest {
        width: 40px !important;
        height: 40px !important;
    }

}

@media (width > 430px) and (width < 959px) {
    .icon-size-medium {
        width: 50px !important;
        height: 50px !important;
    }

    .icon-size-small {
        width: 35px !important;
        height: 35px !important;
    }
}


@media (width <=350px) {
    .icon-size-medium {
        width: 28px !important;
    }

    .icon-size-small {
        width: 30px !important;
    }
}

.frame-div {
    border-radius: 8px;
    border: 2px solid rgb(201, 201, 201);
}

.add-card-button {
    background-color: #FFFFFF;
}

.menu-text {
    font-size: 14px;
    color: #9BA9BD;
}

.card2 {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 130px;
}

.background-media {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    overflow: hidden;
}

.card {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 180px;
}

.video {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 503px;
}

.socials {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 60px;
}

.base {
    cursor: pointer;
}

.base2 {
    cursor: pointer;
}

.sections {
    display: flex;
    flex-direction: column;
    width: 282px;
}

.sections-container {
    display: flex;
    flex-wrap: nowrap;
}

.logo {
    cursor: pointer;
    height: 100px;
    width: 100px;
    background-color: #E8E8E8;
}

.logo2 {
    height: 100px;
    width: 100px;
}

.logo-white {
    height: 100px;
    width: 100px;
    background-color: #FFFFFF;
}

.logo .v-img {
    object-fit: cover;
    width: 100%;
    height: 100%;
}

@media (max-width: 959px) {
    .socials-icon2 {
        width: 30px;
    }

    .logo {
        height: 90px;
        width: 90px;
    }

    .title-harmony {
        font-size: 26px;
    }
}

@media (max-width: 577px) {
    .sections {
        width: 260px;
    }

    .socials-icon2 {
        width: 30px;
    }
}

@media (max-width: 530px) {
    .sections {
        width: 240px;
    }
}

@media (max-width: 496px) {
    .sections {
        width: 225px;
    }

    .socials-icon2 {
        width: 27px;
    }
}

@media (max-width: 462px) {
    .sections {
        width: 208px;
    }
}

@media (max-width: 432px) {
    .sections {
        width: 199px;
    }
}

@media (max-width: 410px) {
    .sections {
        width: 188px;
    }
}

@media (max-width: 410px) {
    .sections {
        width: 188px;
    }
}

@media (max-width: 400px) {
    .sections {
        width: 180px;
    }

    .socials-icon2 {
        width: 24px;
    }
}

@media (max-width: 372px) {
    .sections {
        width: 170px;
    }
}

@media (max-width: 354px) {
    .sections {
        width: 160px;
    }
}

@media (max-width: 330px) {
    .sections {
        width: 150px;
    }
}

@media (max-width: 306px) {
    .sections {
        width: 140px;
    }
}

.page-background {
    position: relative;
    width: 100%;
    min-height: 100vh;
}

.background {
    position: relative;
    z-index: 1;
    background-size: cover;
    background-position: center;
    background-repeat: repeat;
    min-height: 100vh;
}

.background::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-size: cover;
    background-position: center;
    background-repeat: repeat;
    opacity: 0.5;
    z-index: -1;
    width: 100%;
}

.background-opacity::before {
    opacity: 0.5;
}

/* NOTE: '.my-custom-select .v-input__control { display: none }' is dead code in scoped
   context (children carry a different data-v-* than the parent). The ::v-deep variants
   below match and produce the compact 28px v-select rendering. */
.my-custom-select ::v-deep .v-input__control {
    max-height: 28px !important;
    min-height: 28px !important;
}

.my-custom-select ::v-deep .v-select__selections {
    max-height: 50px !important;
    min-height: 50px !important;
    line-height: 28px !important;
    color: #9BA9BD !important;
    font-size: 14px !important;
}

.my-custom-select ::v-deep .v-input__slot {
    max-height: 28px !important;
    min-height: 28px !important;

}

.my-custom-select ::v-deep .v-input__slot .v-input__append-inner {
    margin-top: 1px !important;
    width: 18px !important;
}

.my-custom-select ::v-deep .v-select__input {
    min-height: 28px !important;
    height: 28px !important;
    line-height: 28px !important;
}

.my-custom-select {
    height: 28px !important;
}

.my-custom-select ::v-deep .v-input__icon--append {
    margin-top: auto;
    margin-bottom: auto;
    height: 100%;
    display: flex;
    align-items: center;
}

.my-custom-select ::v-deep .v-input__icon--append .v-icon {
    margin: auto;
}

.my-custom-select ::v-deep .v-list-item--active {
    background-color: #8e84c0 !important;
}

.emoticon {
    cursor: pointer;
}

.wrapper {
    position: relative;
    display: inline-block;
}

.regular-input {
    padding: 0.5rem 1rem;
    border-radius: 3px;
    border: 1px solid #ccc;
    width: 20rem;
    height: 12rem;
    outline: none;
}

.regular-input:focus {
    box-shadow: 0 0 0 3px rgba(66, 153, 225, .5);
}

.emoji-invoker {
    position: absolute;
    top: 0.5rem;
    right: 0.5rem;
    width: 1.5rem;
    height: 1.5rem;
    border-radius: 50%;
    cursor: pointer;
    transition: all 0.2s;
    padding: 0;
    background: transparent;
    border: 0;
}

.emoji-invoker:hover {
    transform: scale(1.1);
}

.emoji-invoker>svg {
    fill: #b1c6d0;
}

.emoji-picker {
    position: relative;
    font-family: Montserrat;
    border: 1px solid #ccc;
    width: 100%;
    max-width: 100%;
    height: 20rem;
    overflow: scroll;
    padding: 1rem;
    box-sizing: border-box;
    border-radius: 0.5rem;
    background: #fff;
    box-shadow: 1px 1px 8px #c7dbe6;
    margin-top: 0.5rem;
}

.emoji-picker__search {
    display: flex;
}

.emoji-picker__search>input {
    flex: 1;
    border-radius: 10rem;
    border: 1px solid #ccc;
    padding: 0.5rem 1rem;
    outline: none;
}

.emoji-picker h5 {
    margin-bottom: 0;
    color: #b1b1b1;
    text-transform: uppercase;
    font-size: 0.8rem;
    cursor: default;
}

.emoji-picker .emojis {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
}

.emoji-picker .emojis:after {
    content: "";
    flex: auto;
}

.emoji-picker .emojis span {
    padding: 0.2rem;
    cursor: pointer;
    border-radius: 5px;
}

.emoji-picker .emojis span:hover {
    background: #ececec;
}

textarea {
    padding: 0.5rem 1rem;
    border-radius: 3px;
    border: 1px solid #ccc;
    width: 100%;
    height: 12rem;
    outline: none;
    box-sizing: border-box;
}

textarea:focus {
    box-shadow: 0 0 0 3px rgba(66, 153, 225, .5);
}

.colors {
    max-height: 98px;
    margin-bottom: 22px !important;
}

.panelColors {
    min-width: 25px !important;
    max-width: 25px !important;
    height: 25px !important;
    min-height: 25px !important;
    border-radius: 100%;
    margin: 2px;
    padding: 0px 0px !important;
    transition: transform 0.2s ease-in-out;
}

.panelColors img {
    width: 100%;
}

.panelColors:hover {
    transform: scale(1.1)
}

.arrow-icon {
    max-width: 20px;
    max-height: 20px;
    transition: transform 0.3s ease-in-out;
}


.plus-social-style {
    width: 32px;
    height: 32px;
    background-color: rgb(255, 255, 255);
    border: 2px solid #8e84c0;
    cursor: pointer;
}

.plus-social-style:hover {
    background-color: #8e84c03b;
}

.setSocialsStyle {
    width: 72px;
    height: 72px;
    display: flex;
}

.modal-social-color {
    overflow: visible !important;
    width: 100%;
    height: 100%;
}

.style-socials {
    width: 24px;
    height: 24px;
}

.style-socials img {
    max-width: 14px;
    max-height: 14px;
}

.style-socials-personalized {
    width: 40px;
    height: 40px;
}

.style-socials-personalized img,
.setSocialsStyle img {
    max-width: 55%;
    max-height: 55%;
}

.style-socials-default {
    width: 40px;
    height: 40px;
}

.style-socials-default img,
.setSocialsStyle img {
    max-width: 75%;
    max-height: 75%;
}

.span-socials {
    max-height: fit-content !important;
    display: flex;
}

.blur-explicit {
    filter: blur(3.5px);
}

.font-anton {
    font-family: 'Anton', sans-serif;
}

.font-bebas-neue {
    font-family: 'Bebas Neue', sans-serif;
}

.font-bree-serif {
    font-family: 'Bree Serif', serif;
}

.font-caveat {
    font-family: 'Caveat', cursive;
}

.font-concert-one {
    font-family: 'Concert One', cursive;
}

.font-courgette {
    font-family: 'Courgette', cursive;
}

.font-creepster {
    font-family: 'Creepster', cursive;
}

.font-dancing-script {
    font-family: 'Dancing Script', cursive;
}

.font-fira-sans-condensed {
    font-family: 'Fira Sans Condensed', sans-serif;
}

.font-indie-flower {
    font-family: 'Indie Flower', cursive;
}

.font-jockey-one {
    font-family: 'Jockey One', sans-serif;
}

.font-orbitron {
    font-family: 'Orbitron', sans-serif;
}

.font-oswald {
    font-family: 'Oswald', sans-serif;
}

.font-pacifico {
    font-family: 'Pacifico', cursive;
}

.font-permanent-marker {
    font-family: 'Permanent Marker', cursive;
}

.font-pixelify-sans {
    font-family: 'Pixelify Sans', sans-serif;
}

.font-protest-strike {
    font-family: 'Protest Strike', cursive;
}

.font-roboto {
    font-family: 'Roboto', sans-serif;
}

.font-teko {
    font-family: 'Teko', sans-serif;
}

.font-titillium-web {
    font-family: 'Titillium Web', sans-serif;
}

.font-rubik {
    font-family: 'Rubik', sans-serif;
}

.list-fonts {
    max-width: 280px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
}

.list-fonts-input {
    max-width: 320.28px;
}

.list-weight-input {
    width: 90px !important;
}

.forgot-password {
    margin-top: 0px;
}

.option-register {
    width: 100%;
    display: flex;
    justify-content: space-between;
    flex-direction: row-reverse;
    align-items: center;
}

.left {
    margin-left: -6px;
    margin-right: 6px;
}

.follow {
    cursor: pointer;
    position: absolute;
    z-index: 999;
    bottom: -10%;
    padding: 0% !important;
    left: 50%;
    transform: translate(-50%);
    background-color: #18FFFF !important;
    color: white;
    width: 25px !important;
    height: 25px !important;
    border: 2px solid white !important;
}

.follow i {
    margin-top: 1px;
}
</style>

<style>
.expansion-content .v-expansion-panel-content__wrap {
    padding: 0 24px 0px !important;
}

.btn-brush:hover {
    color: #0000009d !important;
    caret-color: #0000009d !important;
}

.remove-preview .v-color-picker__dot {
    display: none !important;
}

.p-colorpicker-panel {
    border-radius: 8px
}

.v-dialog {
    overflow: visible !important;
}

.picker-canvas canvas,
.picker-canvas .v-color-picker__canvas {
    height: 100px !important;
}

body {
    margin: 0;
    overflow: hidden;
}

.options .v-expansion-panel-content__wrap {
    padding: 0px !important;
}

.v-picker,
.fit,
.v-dialog {
    width: fit-content !important;
}

.border-timer {
    border: 1px solid #8e84c0;
    border-radius: 5px;
    padding: 10px 15px !important;
}

.remove-preview {
    max-width: 225px !important;
}

.emoji-mart-preview {
    display: none;
    visibility: hidden;
}

.containerSwal {
    max-height: 400px !important;
    padding: 0 0 0.5em !important;
}

.notborder {
    margin: 0.5em auto 0em !important;
    height: 3em !important;
}

.htmlContainerSwal {
    margin: 0.8em 1.6em 0.2em !important;
}

.actionsSwal {
    margin-top: 0.25em !important;
}

.confirmButtonSwal {
    font-size: 0.80em !important;
    border-radius: 0.50em !important;
    background-color: #4CAF50 !important;
}

.cancelButtonSwal {
    font-size: 0.80em !important;
    border-radius: 0.50em !important;
    margin-right: 0.70em !important;
}

.titleSwal {
    line-height: 0.5 !important;
    padding-top: 12px !important;
}

.background-layer {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 0;
    pointer-events: none;
    backface-visibility: hidden;
}

.content-layer {
    position: relative;
    z-index: 1;
}

.format-toolbar {
    margin-bottom: 16px;
}

.format-btn {
    width: 100% !important;
    min-width: auto !important;
    border: 1px solid #9BA9BD !important;
    color: #9BA9BD !important;
}

.format-btn-active {
    background-color: #9BA9BD !important;
    color: white !important;
    border-color: #9BA9BD !important;
}

.format-btn:hover:not(.format-btn-active) {
    background-color: rgba(155, 169, 189, 0.1) !important;
}

.format-btn .v-icon {
    color: inherit !important;
}
</style>
